import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchMachines } from '../store/slices/machinesSlice';
import api from '../services/api';

const StatusBadge = ({ status }) => {
  const colors = {
    in_progress: 'warning',
    completed: 'success',
    draft: 'secondary',
    ready: 'info'
  };

  const labels = {
    in_progress: 'In productie',
    completed: 'Voltooid',
    draft: 'Concept',
    ready: 'Gereed'
  };

  return (
    <span className={`badge bg-${colors[status] || 'secondary'}`}>
      {labels[status] || status}
    </span>
  );
};

const MachineStatusBadge = ({ status }) => {
  const colors = {
    idle: 'secondary',
    active: 'success',
    maintenance: 'warning',
    error: 'danger',
    offline: 'dark'
  };

  return (
    <span className={`badge bg-${colors[status] || 'secondary'}`}>
      {status === 'active' ? 'Actief' :
       status === 'idle' ? 'Inactief' :
       status === 'maintenance' ? 'Onderhoud' :
       status === 'error' ? 'Storing' : status}
    </span>
  );
};

const Production = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { machines, loading: machinesLoading } = useSelector((state) => state.machines);
  const [productionData, setProductionData] = useState({});
  const [loading, setLoading] = useState(true);

  // Restore filters from URL params first, then sessionStorage, then defaults
  const getInitialFilters = () => {
    if (searchParams.toString()) {
      return {
        filter: searchParams.get('status') || 'in_progress',
        dateFilter: searchParams.get('period') || 'all'
      };
    }
    try {
      const saved = sessionStorage.getItem('productionFilters');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return { filter: 'in_progress', dateFilter: 'all' };
  };

  const [filters, setFiltersState] = useState(getInitialFilters);
  const filter = filters.filter;
  const dateFilter = filters.dateFilter;

  const setFilters = useCallback((newFilters) => {
    setFiltersState(newFilters);
    sessionStorage.setItem('productionFilters', JSON.stringify(newFilters));
    const params = new URLSearchParams();
    if (newFilters.filter && newFilters.filter !== 'in_progress') params.set('status', newFilters.filter);
    if (newFilters.dateFilter && newFilters.dateFilter !== 'all') params.set('period', newFilters.dateFilter);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // On mount: sync initial filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.filter && filters.filter !== 'in_progress') params.set('status', filters.filter);
    if (filters.dateFilter && filters.dateFilter !== 'all') params.set('period', filters.dateFilter);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(fetchMachines());
  }, [dispatch]);

  useEffect(() => {
    if (machines && machines.length > 0) {
      fetchProductionData();
    }
  }, [machines, filter, dateFilter]);

  const fetchProductionData = async () => {
    setLoading(true);
    try {
      // Fetch cutlists that are in_progress or completed
      const statusFilter = filter === 'all' ? 'in_progress,completed' : filter;
      const response = await api.get(`/cutlists?status=${statusFilter}&limit=100`);

      // Group by machine
      const grouped = {};

      // Initialize all machines
      machines.forEach(machine => {
        grouped[machine._id] = {
          machine,
          cutLists: [],
          inProgress: 0,
          completed: 0,
          totalItems: 0
        };
      });

      // Group cutlists by machine
      const cutLists = response.data.cutLists || [];
      cutLists.forEach(cutList => {
        const machineId = cutList.machine?._id || cutList.machine;
        if (machineId && grouped[machineId]) {
          // Date filtering
          if (dateFilter !== 'all') {
            const cutListDate = new Date(cutList.startedAt || cutList.createdAt);
            const now = new Date();

            if (dateFilter === 'today') {
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              if (cutListDate < today) return;
            } else if (dateFilter === 'week') {
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              if (cutListDate < weekAgo) return;
            }
          }

          grouped[machineId].cutLists.push(cutList);
          if (cutList.status === 'in_progress') {
            grouped[machineId].inProgress++;
          } else if (cutList.status === 'completed') {
            grouped[machineId].completed++;
          }
          grouped[machineId].totalItems += cutList.items?.length || 0;
        }
      });

      setProductionData(grouped);
    } catch (error) {
      console.error('Error fetching production data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async (cutListId) => {
    try {
      await api.patch(`/cutlists/${cutListId}/status`, { status: 'completed' });
      fetchProductionData();
      dispatch(fetchMachines()); // Refresh machine data
    } catch (error) {
      console.error('Error marking cutlist as completed:', error);
    }
  };

  // Calculate totals
  const totals = Object.values(productionData).reduce((acc, data) => ({
    inProgress: acc.inProgress + data.inProgress,
    completed: acc.completed + data.completed,
    totalItems: acc.totalItems + data.totalItems,
    totalCutLists: acc.totalCutLists + data.cutLists.length
  }), { inProgress: 0, completed: 0, totalItems: 0, totalCutLists: 0 });

  if (machinesLoading || loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="production-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Productie Overzicht</h1>
          <p className="text-muted">Alle productie orders per machine</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={fetchProductionData}
          >
            <i className="bi bi-arrow-repeat me-2"></i>
            Vernieuwen
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-warning bg-opacity-10 border-warning">
            <div className="card-body text-center">
              <h3 className="text-warning mb-0">{totals.inProgress}</h3>
              <small className="text-muted">In productie</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success bg-opacity-10 border-success">
            <div className="card-body text-center">
              <h3 className="text-success mb-0">{totals.completed}</h3>
              <small className="text-muted">Voltooid</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0">{totals.totalCutLists}</h3>
              <small className="text-muted">Totaal snijlijsten</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0">{totals.totalItems}</h3>
              <small className="text-muted">Totaal items</small>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filter}
                onChange={(e) => setFilters({ ...filters, filter: e.target.value })}
              >
                <option value="all">Alle (in productie + voltooid)</option>
                <option value="in_progress">Alleen in productie</option>
                <option value="completed">Alleen voltooid</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Periode</label>
              <select
                className="form-select"
                value={dateFilter}
                onChange={(e) => setFilters({ ...filters, dateFilter: e.target.value })}
              >
                <option value="today">Vandaag</option>
                <option value="week">Afgelopen week</option>
                <option value="all">Alles</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Machine Production Tables */}
      {machines.map((machine) => {
        const data = productionData[machine._id] || { cutLists: [], inProgress: 0, completed: 0 };

        return (
          <div key={machine._id} className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <h5 className="mb-0">
                  <Link to={`/machines/${machine._id}`} className="text-decoration-none">
                    {machine.name}
                  </Link>
                </h5>
                <MachineStatusBadge status={machine.status} />
                {machine.currentJob && (
                  <span className="badge bg-info">
                    <i className="bi bi-play-fill me-1"></i>
                    Actieve job
                  </span>
                )}
              </div>
              <div className="d-flex gap-2">
                <span className="badge bg-warning text-dark">
                  {data.inProgress} in productie
                </span>
                <span className="badge bg-success">
                  {data.completed} voltooid
                </span>
              </div>
            </div>

            {data.cutLists.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Snijlijst</th>
                      <th>Naam</th>
                      <th>Items</th>
                      <th>Status</th>
                      <th>Gestart</th>
                      <th>Voltooid</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cutLists.map((cutList) => (
                      <tr key={cutList._id} className={cutList.status === 'in_progress' ? 'table-warning' : ''}>
                        <td>
                          <Link to={`/cutlists/${cutList._id}`}>
                            <code>{cutList.cutListId}</code>
                          </Link>
                        </td>
                        <td>
                          <Link to={`/cutlists/${cutList._id}`}>
                            {cutList.name || '-'}
                          </Link>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {cutList.items?.length || 0} items
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={cutList.status} />
                        </td>
                        <td className="text-muted small">
                          {cutList.startedAt
                            ? new Date(cutList.startedAt).toLocaleString('nl-NL', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </td>
                        <td className="text-muted small">
                          {cutList.completedAt
                            ? new Date(cutList.completedAt).toLocaleString('nl-NL', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </td>
                        <td>
                          {cutList.status === 'in_progress' && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleMarkCompleted(cutList._id)}
                              title="Markeer als voltooid"
                            >
                              <i className="bi bi-check-lg"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-body text-center text-muted py-4">
                <i className="bi bi-inbox fs-4 d-block mb-2"></i>
                Geen productie orders voor deze machine
              </div>
            )}
          </div>
        );
      })}

      {machines.length === 0 && (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Geen machines geconfigureerd. <Link to="/machines/new">Voeg een machine toe</Link>.
        </div>
      )}
    </div>
  );
};

export default Production;
